import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Volume2, Square, Loader2, Repeat } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_KEY = 'deal_noah_narrate';

/** Botón para que Noah narre un texto con su voz (OpenAI TTS vía Emergent LLM Key). */
export const NoahVoiceButton = ({ text, label = 'Escuchar a Noah', autoOnMount = true, testId = 'noah-voice-btn' }) => {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [auto, setAuto] = useState(() => localStorage.getItem(AUTO_KEY) === '1');
  const audioRef = useRef(null);
  const triedRef = useRef(false);

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const stop = () => {
    audioRef.current?.pause();
    setPlaying(false);
  };

  const speak = useCallback(async (silentFail = false) => {
    if (!text || loading) return;
    if (audioRef.current && !audioRef.current.paused) { stop(); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/noah/speak`, { text });
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => { setPlaying(false); if (!silentFail) toast.error('No se pudo reproducir el audio'); };
      await audio.play();
      setPlaying(true);
    } catch (e) {
      if (!silentFail) toast.error(e.response?.data?.detail || 'Noah no pudo hablar ahora mismo');
    } finally {
      setLoading(false);
    }
  }, [text, loading]);

  // Narración automática al abrir (si el navegador lo permite sin interacción)
  useEffect(() => {
    if (autoOnMount && auto && text && !triedRef.current) {
      triedRef.current = true;
      speak(true);
    }
  }, [auto, text, autoOnMount, speak]);

  const toggleAuto = () => {
    const next = !auto;
    setAuto(next);
    localStorage.setItem(AUTO_KEY, next ? '1' : '0');
    toast.info(next ? 'Noah narrará el resumen al abrir la app' : 'Narración automática desactivada');
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => (playing ? stop() : speak())}
        disabled={!text || loading}
        data-testid={testId}
        title={label}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-40 transition-colors text-[11px] font-medium"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : playing ? <Square className="w-3.5 h-3.5" />
            : <Volume2 className="w-3.5 h-3.5" />}
        {loading ? 'Grabando…' : playing ? 'Detener' : 'Escuchar'}
      </button>
      <button
        onClick={toggleAuto}
        data-testid={`${testId}-auto`}
        title={auto ? 'Narración automática activada' : 'Activar narración automática al abrir'}
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
          auto ? 'bg-brand-copper text-white' : 'text-muted-foreground hover:bg-muted'
        }`}
      >
        <Repeat className="w-3 h-3" />
      </button>
    </div>
  );
};

export default NoahVoiceButton;
